import _ from 'underscore';

import { ExploreContentApi_ContentType, IContent } from '~src/spacesPlatform/models/IContent';

import { appendDraftIdPrefix } from '../../../drafts/utils/DraftUtils';
import { ILocationInfo } from '../../../personalSpace/model/ILocationInfo';
import { UserMapper } from '../../../userManagement/users/models/mappers/UserMapper';
import { IUser } from '../../../userManagement/users/serverApi/types/IUser';
import { ExplorableItemType } from '../ContentType';
import { IContentInfo } from '../IContentInfo';
import { INamedItemInfo } from '../resourceInfoParts';
import { MapperHelper } from './MapperHelper';

export namespace ContentMapper {

  export function map(content: IContent[], users: _.Dictionary<IUser>): IContentInfo[] {
    return _.map(content, (c: IContent): IContentInfo<typeof c.contentType> => {
      const contentInfo = {
        // Explore API returns a number for id which can cause equaliy mismatches with getProjects/etc APIs
        // So cast id to a string here so it will match all existing APIs
        id: c.id ? '' + c.id : appendDraftIdPrefix(c.draftId), // drafts with 'unpublished' status don't have an id so map to draftId with prefix for uniqueness
        luid: c.luid || null,
        // TODO: Remove this hack after TFSID 963146 is resolved
        contentType: mapContentType(c.contentType),
        name: c.name,
        description: c.description,
        owner: UserMapper.mapOne(users[c.ownerId]),
        createdAt: MapperHelper.toDate(c.createdAt),
        updatedAt: MapperHelper.toDate(c.updatedAt),
        favorite: c.isFavorite,
        favoritesTotal: c.favoritesTotal,
        thumbnailUrl: c.thumbnailUrl,
        tags: c.tags || [],
        contentDetail: tryParseJson(c.contentDetail),
        draftLuid: c.draftLuid ? c.draftLuid : null,
        draftId: c.draftId ? c.draftId : null,
        draftStatus: c.draftStatus ? c.draftStatus : null,
        extractDraftAvailable: c.extractDraftAvailable
      };

      if (c.contentType === 'database') {
        return {
        ...contentInfo,
        vizportalId: c.id,
        __typename: 'DatabaseServer',
        projectName: c.projectName ?? c.containerName
        };
      }else if (c.contentType === 'table') {
        return { ...contentInfo, vizportalId: c.id, __typename: 'DatabaseTable' };
      }
      return contentInfo;
    });
  }

  export const mapContentType = (contentType: ExplorableItemType): ExplorableItemType => {
    switch (contentType.toString()) {
      case 'customizedview':
        return 'customView';
      case 'datarole':
        return 'dataRole';
      case 'virtualconnection':
        return 'virtualConnection';
      case 'virtualconnectiontable':
        return 'virtualConnectionTable';
      case 'predictivemodel':
        return 'predictiveModel';
      default:
        return contentType;
    }
  };

  function tryParseJson(jsonString: string): any {
    if (!jsonString) {
      return null;
    }

    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return null;
    }
  }

  export const ContentTypeToTargetMap: Record<ExploreContentApi_ContentType, string> = {
    project: 'PROJECT',
    workbook: 'WORKBOOK',
    datasource: 'DATASOURCE',
    dataRole: 'DATAROLE',
    lens: 'LENS',
    view: 'VIEW',
    dataconnection: '',
    flow: 'FLOW',
    databaseWithCrass: '',
    tableWithCrass: '',
    database: 'DATABASE',
    table: 'TABLE',
    metric: 'METRIC',
    virtualConnection: 'VIRTUALCONNECTION',
    virtualConnectionTable: 'VIRTUALCONNECTIONTABLE',
    virtualConnectionColumn: 'VIRTUALCONNECTIONCOLUMN',
    collection: 'COLLECTION',
    predictiveModel: 'PREDICTIVEMODEL',
  };

  export function getContentToProjectMap(contentType: string, projects: _.Dictionary<INamedItemInfo>) {
    const contentToProject: _.Dictionary<INamedItemInfo> = {};
    _.forEach(projects, (project) => {
      _.forEach(project.contents[contentType], (content: number) => {
        contentToProject[content] = project;
      });
    });
    return contentToProject;
  }

  export function getContentToLocationMap(contentType: string, locations: ILocationInfo[]) {
    const contentToLocation: _.Dictionary<ILocationInfo> = {};
    _.forEach(locations, (location) => {
      if (location.contents) {
        _.forEach(location.contents[contentType], (content: number) => {
          contentToLocation[content] = location;
        });
      }
    });
    return contentToLocation;
  }

}
